import React, { useState, useEffect } from 'react';
import { ShieldCheck, Sun, Moon, ChevronDown, Check, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom'; // ✅ Correct

const INITIAL_PRICES = [
  { symbol: 'BTC/USD', price: 92450.20, change: 2.4 },
  { symbol: 'ETH/USD', price: 3420.15, change: 1.8 },
  { symbol: 'SOL/USD', price: 198.80, change: 5.1 },
  { symbol: 'BNB/USD', price: 612.40, change: -0.4 },
  { symbol: 'POL/USD', price: 0.78, change: 0.9 },
  { symbol: 'AVAX/USD', price: 38.90, change: 3.2 },
];

export default function Header({
  darkMode,
  setDarkMode,
  isChainDropdownOpen,
  setIsChainDropdownOpen,
  selectedChain,
  setSelectedChain,
  CHAINS,
  connectedWallet,
  handleDisconnect,
  handleOpenSyncModal
}) {
  const [tickerPrices, setTickerPrices] = useState(INITIAL_PRICES);

  useEffect(() => {
    const fetchLivePrices = async () => {
      try {
        const response = await fetch(
          'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,solana,binancecoin,polygon-ecosystem-token,avalanche-2&vs_currencies=usd&include_24hr_change=true'
        );
        const data = await response.json();

        if (data) {
          setTickerPrices([
            {
              symbol: 'BTC/USD',
              price: data.bitcoin?.usd || INITIAL_PRICES[0].price,
              change: data.bitcoin?.usd_24h_change || INITIAL_PRICES[0].change,
            },
            {
              symbol: 'ETH/USD',
              price: data.ethereum?.usd || INITIAL_PRICES[1].price,
              change: data.ethereum?.usd_24h_change || INITIAL_PRICES[1].change,
            },
            {
              symbol: 'SOL/USD',
              price: data.solana?.usd || INITIAL_PRICES[2].price,
              change: data.solana?.usd_24h_change || INITIAL_PRICES[2].change,
            },
            {
              symbol: 'BNB/USD',
              price: data.binancecoin?.usd || INITIAL_PRICES[3].price,
              change: data.binancecoin?.usd_24h_change || INITIAL_PRICES[3].change,
            },
            {
              symbol: 'POL/USD',
              price: data['polygon-ecosystem-token']?.usd || INITIAL_PRICES[4].price,
              change: data['polygon-ecosystem-token']?.usd_24h_change || INITIAL_PRICES[4].change,
            },
            {
              symbol: 'AVAX/USD',
              price: data['avalanche-2']?.usd || INITIAL_PRICES[5].price,
              change: data['avalanche-2']?.usd_24h_change || INITIAL_PRICES[5].change,
            },
          ]);
        }
      } catch (error) {
        console.error('Error fetching live crypto prices:', error);
      }
    };

    fetchLivePrices();
    const interval = setInterval(fetchLivePrices, 30000); // Refreshes every 30s
    return () => clearInterval(interval);
  }, []);

  return (
    <header className="sticky top-0 z-40 bg-white/80 dark:bg-[#0d1322]/90 backdrop-blur-md border-b border-slate-200 dark:border-slate-800/80">
      {/* Live Crypto Price Ticker */}
      <div className="w-full h-9 sm:h-10 bg-slate-100 dark:bg-[#090d18] border-b border-slate-200 dark:border-slate-800/40 flex items-center overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap text-[11px] sm:text-xs font-mono text-slate-600 dark:text-slate-400 gap-6 sm:gap-8 px-4">
          {tickerPrices.map((item) => {
            const isPositive = item.change >= 0;
            return (
              <span key={item.symbol} className="flex items-center gap-1 sm:gap-1.5">
                <span className="font-bold text-slate-900 dark:text-slate-200">
                  {item.symbol}:
                </span>{' '}
                ${item.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                <span className={isPositive ? 'text-emerald-500' : 'text-rose-500'}>
                  {isPositive ? `+${item.change.toFixed(1)}%` : `${item.change.toFixed(1)}%`}
                </span>
              </span>
            );
          })}
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
        {/* Brand Section */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-tr from-cyan-500 via-indigo-500 to-purple-600 p-[2px] flex items-center justify-center shadow-lg shadow-cyan-500/20 shrink-0">
            <div className="w-full h-full bg-white dark:bg-[#0b0f19] rounded-[10px] flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-500 dark:text-cyan-400" />
            </div>
          </div>
<a href="/" className="block hover:opacity-95 transition-opacity">
  <div>
    <div className="flex items-center gap-1.5 sm:gap-2">
      <span className="font-bold text-base sm:text-lg bg-gradient-to-r from-cyan-600 via-indigo-600 to-purple-600 dark:from-cyan-400 dark:via-indigo-300 dark:to-purple-400 bg-clip-text text-transparent">
        SyncProtocol
      </span>
      <span className="hidden sm:inline-block text-[10px] font-mono px-2 py-0.5 rounded-full bg-cyan-100 dark:bg-cyan-950/80 text-cyan-700 dark:text-cyan-400 border border-cyan-300 dark:border-cyan-800/50">
        v2.4.0 Node
      </span>
    </div>
    <div className="text-[10px] sm:text-[11px] font-medium text-slate-500 dark:text-slate-400 tracking-wide">
      By <span className="text-cyan-600 dark:text-cyan-400 font-semibold">Tickethelps.com</span>
    </div>
  </div>
</a>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-1.5 sm:gap-3">
          {/* Dark / Light Switch */}
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-1.5 sm:p-2 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:text-cyan-500 transition-colors shrink-0"
            title="Toggle theme"
          >
            {darkMode ? <Sun className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" /> : <Moon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-700" />}
          </button>

          {/* Chain Selector */}
          {/* <div className="relative shrink-0">
            <button
              onClick={() => setIsChainDropdownOpen(!isChainDropdownOpen)}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-700/60 hover:border-cyan-500/50 transition-all text-xs font-medium text-slate-800 dark:text-slate-200"
            >
              <span className="text-xs sm:text-sm">{selectedChain.icon}</span>
              <span className="hidden md:inline">{selectedChain.name}</span>
              <span className="md:hidden text-[11px]">{selectedChain.symbol}</span>
              <ChevronDown className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-400" />
            </button>

            {isChainDropdownOpen && (
              <div className="absolute right-0 mt-2 w-44 sm:w-48 rounded-xl bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-slate-700/80 shadow-2xl py-1 z-50 backdrop-blur-xl">
                <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                  Select Network
                </div>
                {CHAINS.map(chain => (
                  <button
                    key={chain.id}
                    onClick={() => {
                      setSelectedChain(chain);
                      setIsChainDropdownOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-xs text-left transition-colors ${
                      selectedChain.id === chain.id 
                        ? 'bg-cyan-50 dark:bg-cyan-500/10 text-cyan-600 dark:text-cyan-300 font-medium' 
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span>{chain.icon}</span>
                      <span>{chain.name}</span>
                    </span>
                    {selectedChain.id === chain.id && <Check className="w-3.5 h-3.5 text-cyan-500" />}
                  </button>
                ))}
              </div>
            )}
          </div> */}

          {/* Connect Wallet Button */}
          {connectedWallet ? (
            <button
              onClick={handleDisconnect}
              className="px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-xl bg-emerald-50 dark:bg-slate-900 border border-emerald-500/40 text-emerald-600 dark:text-emerald-400 text-xs font-semibold flex items-center gap-1.5 sm:gap-2 hover:bg-emerald-100 dark:hover:bg-emerald-950/30 transition-all shrink-0"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span>{connectedWallet.slice(0, 6)}...</span>
            </button>
          ) : (
            <button
              onClick={() => handleOpenSyncModal(null)}
              className="relative group overflow-hidden rounded-xl p-[1px] font-semibold text-xs transition-all shadow-lg shadow-cyan-500/10 hover:shadow-cyan-500/25 active:scale-95 shrink-0"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-indigo-500 to-purple-600 transition-all group-hover:opacity-90"></span>
              <span className="relative px-2.5 sm:px-4 py-1.5 sm:py-2.5 rounded-[11px] bg-white dark:bg-[#0b0f19] flex items-center gap-1.5 sm:gap-2 transition-all group-hover:bg-opacity-0">
                <Wallet className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cyan-500 dark:text-cyan-400 group-hover:text-white transition-colors" />
                <span className="text-slate-900 dark:text-white group-hover:text-white text-[11px] sm:text-xs">
                  <span className="hidden sm:inline">Connect Wallet</span>
                  <span className="sm:hidden">Connect</span>
                </span>
              </span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
}